import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { Atom } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import {
    getLeafColumnInfos,
    resolveDefaultColumnState,
} from "./columns/columnLayout";
import type { DataGridColumn } from "./columns/types";
import type {
    DataGridColumnState,
    DataGridReactTable,
    UseDataGridOptions,
} from "./types";

interface UseColumnStateSyncInput<TData extends RowData> {
    table: DataGridReactTable<TData>;
    columnVariants: Atom<Record<string, string>>;
    columnState: Partial<DataGridColumnState> | undefined;
    optionsRef: { readonly current: UseDataGridOptions<TData> };
}

const isSameColumnState = (a: DataGridColumnState, b: DataGridColumnState) =>
    a.order === b.order &&
    a.sizing === b.sizing &&
    a.visibility === b.visibility &&
    a.pinning === b.pinning &&
    a.variants === b.variants;

const readColumnState = <TData extends RowData>({
    atoms,
    columnVariants,
}: {
    atoms: DataGridReactTable<TData>["atoms"];
    columnVariants: Atom<Record<string, string>>;
}): DataGridColumnState => ({
    order: atoms.columnOrder.get(),
    sizing: atoms.columnSizing.get(),
    visibility: atoms.columnVisibility.get(),
    pinning: atoms.columnPinning.get(),
    variants: columnVariants.get(),
});

/**
 * Two-way sync for column order, sizing, visibility, pinning, and variants. A
 * new `columnState` object is applied to the table; table changes are reported
 * through `onColumnStateChange`, with sizing held back until a resize drag
 * ends so callers never re-render per drag frame. Applying a `columnState`
 * reports nothing, including the half-applied states between its setters.
 */
export const useColumnStateSync = <TData extends RowData>({
    table,
    columnVariants,
    columnState,
    optionsRef,
}: UseColumnStateSyncInput<TData>) => {
    const appliedRef = useRef(columnState);
    const isApplyingRef = useRef(false);
    const lastRef = useRef<DataGridColumnState | null>(null);
    const atoms = table.atoms;

    useLayoutEffect(() => {
        if (!columnState || appliedRef.current === columnState) {
            return;
        }
        appliedRef.current = columnState;
        isApplyingRef.current = true;
        try {
            if (columnState.order) {
                table.setColumnOrder(columnState.order);
            }
            if (columnState.sizing) {
                table.setColumnSizing(columnState.sizing);
            }
            if (columnState.visibility) {
                table.setColumnVisibility(columnState.visibility);
            }
            if (columnState.pinning) {
                table.setColumnPinning(columnState.pinning);
            }
            if (columnState.variants) {
                columnVariants.set(columnState.variants);
            }
        } finally {
            isApplyingRef.current = false;
        }
        lastRef.current = readColumnState({ atoms, columnVariants });
    }, [atoms, columnState, columnVariants, table]);

    useEffect(() => {
        lastRef.current = readColumnState({ atoms, columnVariants });
        const emit = () => {
            if (
                isApplyingRef.current ||
                atoms.columnResizing.get().isResizingColumn
            ) {
                return;
            }
            const next = readColumnState({ atoms, columnVariants });
            if (lastRef.current && isSameColumnState(next, lastRef.current)) {
                return;
            }
            lastRef.current = next;
            optionsRef.current.onColumnStateChange?.(next);
        };
        const subscriptions = [
            atoms.columnOrder.subscribe(emit),
            atoms.columnSizing.subscribe(emit),
            atoms.columnVisibility.subscribe(emit),
            atoms.columnPinning.subscribe(emit),
            atoms.columnResizing.subscribe(emit),
            columnVariants.subscribe(emit),
        ];
        return () => {
            for (const subscription of subscriptions) {
                subscription.unsubscribe();
            }
        };
    }, [atoms, columnVariants, optionsRef]);
};

/** `defaultColumnState` completed from the definitions. */
export const useDefaultColumnState = <TData extends RowData>({
    columns,
    declared,
}: {
    columns: ReadonlyArray<DataGridColumn<TData>>;
    declared: Partial<DataGridColumnState> | undefined;
}) =>
    useMemo(
        () =>
            resolveDefaultColumnState({
                leaves: getLeafColumnInfos(columns),
                declared,
            }),
        [columns, declared],
    );
